/**
 * 门客系统状态管理
 */

import { create } from 'zustand'
import type {
  RetainerState,
  Retainer,
  RetainerEvent,
  RetainerType,
  MissionType
} from '../types/retainer'

// 初始状态
const initialState: RetainerState = {
  retainers: new Map(),
  missions: new Map(),
  recruitmentPlans: new Map(),
  events: new Map(),
  statistics: {
    totalRetainers: 0,
    totalMissions: 0,
    completedMissions: 0,
    failedMissions: 0,
    totalRecruitmentPlans: 0,
    successfulRecruitments: 0
  }
}

// 预设门客模板
const retainerTemplates: Record<RetainerType, Omit<Retainer, 'id' | 'characterId' | 'name' | 'factionId' | 'hiredAt' | 'lastActiveAt'>> = {
  scholar: {
    type: 'scholar',
    quality: 'common',
    attributes: {
      intelligence: 70,
      strength: 30,
      charisma: 50,
      cunning: 40,
      craftsmanship: 30,
      loyalty: 60
    },
    status: 'idle',
    level: 1,
    experience: 0,
    skills: [
      { name: '学术研究', level: 1, description: '提升研究效率' },
      { name: '教育', level: 1, description: '提升训练效果' }
    ],
    equipment: [],
    salary: {
      base: 50,
      bonus: 10
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  warrior: {
    type: 'warrior',
    quality: 'common',
    attributes: {
      intelligence: 40,
      strength: 80,
      charisma: 50,
      cunning: 30,
      craftsmanship: 30,
      loyalty: 70
    },
    status: 'idle',
    level: 1,
    experience: 0,
    skills: [
      { name: '战斗', level: 1, description: '提升战斗能力' },
      { name: '训练', level: 1, description: '提升士兵训练效果' }
    ],
    equipment: [],
    salary: {
      base: 60,
      bonus: 15
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  merchant: {
    type: 'merchant',
    quality: 'common',
    attributes: {
      intelligence: 60,
      strength: 40,
      charisma: 70,
      cunning: 60,
      craftsmanship: 40,
      loyalty: 50
    },
    status: 'idle',
    level: 1,
    experience: 0,
    skills: [
      { name: '贸易', level: 1, description: '提升贸易收益' },
      { name: '理财', level: 1, description: '提升财务管理能力' }
    ],
    equipment: [],
    salary: {
      base: 70,
      bonus: 20
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  strategist: {
    type: 'strategist',
    quality: 'rare',
    attributes: {
      intelligence: 90,
      strength: 30,
      charisma: 60,
      cunning: 80,
      craftsmanship: 20,
      loyalty: 60
    },
    status: 'idle',
    level: 10,
    experience: 0,
    skills: [
      { name: '战略规划', level: 3, description: '提升战略决策效果' },
      { name: '情报分析', level: 3, description: '提升情报收集效果' }
    ],
    equipment: [],
    salary: {
      base: 150,
      bonus: 50
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  diplomat: {
    type: 'diplomat',
    quality: 'common',
    attributes: {
      intelligence: 70,
      strength: 30,
      charisma: 80,
      cunning: 60,
      craftsmanship: 20,
      loyalty: 55
    },
    status: 'idle',
    level: 1,
    experience: 0,
    skills: [
      { name: '外交谈判', level: 1, description: '提升外交效果' },
      { name: '礼仪', level: 1, description: '提升文化影响力' }
    ],
    equipment: [],
    salary: {
      base: 65,
      bonus: 15
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  spy: {
    type: 'spy',
    quality: 'rare',
    attributes: {
      intelligence: 80,
      strength: 50,
      charisma: 50,
      cunning: 90,
      craftsmanship: 30,
      loyalty: 40
    },
    status: 'idle',
    level: 1,
    experience: 0,
    skills: [
      { name: '潜行', level: 1, description: '提升隐蔽能力' },
      { name: '情报收集', level: 1, description: '提升情报获取效率' }
    ],
    equipment: [],
    salary: {
      base: 80,
      bonus: 20
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  craftsman: {
    type: 'craftsman',
    quality: 'common',
    attributes: {
      intelligence: 50,
      strength: 60,
      charisma: 30,
      cunning: 30,
      craftsmanship: 90,
      loyalty: 65
    },
    status: 'idle',
    level: 1,
    experience: 0,
    skills: [
      { name: '工艺制作', level: 1, description: '提升生产效率' },
      { name: '建筑', level: 1, description: '提升建筑速度' }
    ],
    equipment: [],
    salary: {
      base: 45,
      bonus: 10
    },
    history: {
      missions: [],
      achievements: []
    }
  },
  advisor: {
    type: 'advisor',
    quality: 'epic',
    attributes: {
      intelligence: 85,
      strength: 30,
      charisma: 75,
      cunning: 70,
      craftsmanship: 20,
      loyalty: 70
    },
    status: 'idle',
    level: 5,
    experience: 0,
    skills: [
      { name: '谋略', level: 2, description: '提升决策质量' },
      { name: '治国', level: 2, description: '提升内政效率' }
    ],
    equipment: [],
    salary: {
      base: 120,
      bonus: 40
    },
    history: {
      missions: [],
      achievements: []
    }
  }
}

interface RetainerStore extends RetainerState {
  // 门客管理
  hireRetainer: (name: string, type: RetainerType, factionId: string) => string
  assignMission: (retainerId: string, missionId: string) => void
  recallRetainer: (retainerId: string) => void
  levelUpRetainer: (retainerId: string) => void
  
  // 任务管理
  createMission: (name: string, type: MissionType, description: string, difficulty: number) => string
  completeMission: (missionId: string, success: boolean) => void
  
  // 招募管理
  startRecruitment: (factionId: string, location: string, budget: number) => string
  
  // 事件管理
  createEvent: (retainerId: string, type: RetainerEvent['type'], description: string, details: Record<string, any>) => void
  
  // 统计
  updateStatistics: () => void
}

export const useRetainerStore = create<RetainerStore>((set) => ({
  ...initialState,
  
  // 门客管理
  hireRetainer: (name, type, factionId) => {
    const id = `retainer_${Date.now()}`
    const hiredAt = { year: 1046, month: 1, day: 1 }
    const template = retainerTemplates[type]

    if (template) {
      set(state => {
        const newRetainers = new Map(state.retainers)
        newRetainers.set(id, {
          ...template,
          id,
          characterId: `char_${name.toLowerCase().replace(/\s+/g, '_')}`,
          name,
          factionId,
          hiredAt,
          lastActiveAt: hiredAt
        })
        
        return {
          retainers: newRetainers,
          statistics: {
            ...state.statistics,
            totalRetainers: state.statistics.totalRetainers + 1
          }
        }
      })
      
      return id
    }
    
    return ''
  },
  
  assignMission: (retainerId, missionId) => {
    set(state => {
      const newRetainers = new Map(state.retainers)
      const newMissions = new Map(state.missions)
      
      const retainer = newRetainers.get(retainerId)
      const mission = newMissions.get(missionId)
      
      if (retainer && mission) {
        newRetainers.set(retainerId, {
          ...retainer,
          status: 'on_mission',
          currentMissionId: missionId,
          lastActiveAt: { year: 1046, month: 1, day: 1 }
        })
        
        newMissions.set(missionId, {
          ...mission,
          status: 'in_progress',
          assigneeId: retainerId,
          startedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return {
          retainers: newRetainers,
          missions: newMissions
        }
      }
      
      return state
    })
  },
  
  recallRetainer: (retainerId) => {
    set(state => {
      const newRetainers = new Map(state.retainers)
      const retainer = newRetainers.get(retainerId)
      
      if (retainer && retainer.currentMissionId) {
        const newMissions = new Map(state.missions)
        const mission = newMissions.get(retainer.currentMissionId)
        
        if (mission) {
          newMissions.set(retainer.currentMissionId, {
            ...mission,
            status: 'cancelled'
          })
        }
        
        newRetainers.set(retainerId, {
          ...retainer,
          status: 'idle',
          currentMissionId: undefined,
          lastActiveAt: { year: 1046, month: 1, day: 1 }
        })
        
        return {
          retainers: newRetainers,
          missions: newMissions
        }
      }
      
      return state
    })
  },
  
  levelUpRetainer: (retainerId) => {
    set(state => {
      const newRetainers = new Map(state.retainers)
      const retainer = newRetainers.get(retainerId)
      
      if (retainer) {
        newRetainers.set(retainerId, {
          ...retainer,
          level: retainer.level + 1,
          experience: 0,
          lastActiveAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { retainers: newRetainers }
      }
      
      return state
    })
  },
  
  // 任务管理
  createMission: (name, type, description, difficulty) => {
    const id = `mission_${Date.now()}`

    set(state => {
      const newMissions = new Map(state.missions)
      newMissions.set(id, {
        id,
        name,
        type,
        description,
        difficulty,
        target: {},
        rewards: {
          experience: difficulty * 10,
          money: difficulty * 50,
          reputation: difficulty * 5,
          items: []
        },
        penalties: {
          injury: difficulty > 5,
          capture: difficulty > 7,
          death: difficulty > 9,
          reputation: difficulty * 2
        },
        requirements: {},
        duration: difficulty * 2,
        status: 'available',
        result: {
          success: false,
          details: '',
          rewardsGiven: false
        }
      })
      
      return {
        missions: newMissions,
        statistics: {
          ...state.statistics,
          totalMissions: state.statistics.totalMissions + 1
        }
      }
    })
    
    return id
  },
  
  completeMission: (missionId, success) => {
    set(state => {
      const newMissions = new Map(state.missions)
      const newRetainers = new Map(state.retainers)
      const mission = newMissions.get(missionId)
      
      if (mission && mission.assigneeId) {
        const retainer = newRetainers.get(mission.assigneeId)
        
        if (retainer) {
          let newRetainer = { ...retainer }
          
          if (success) {
            newRetainer = {
              ...retainer,
              experience: retainer.experience + mission.rewards.experience,
              history: {
                ...retainer.history,
                missions: [...retainer.history.missions, missionId]
              }
            }
          }
          
          newRetainer = {
            ...newRetainer,
            status: 'idle',
            currentMissionId: undefined,
            lastActiveAt: { year: 1046, month: 1, day: 1 }
          }
          
          newRetainers.set(mission.assigneeId, newRetainer)
        }
        
        newMissions.set(missionId, {
          ...mission,
          status: success ? 'completed' : 'failed',
          completedAt: { year: 1046, month: 1, day: 1 },
          result: {
            success,
            details: success ? '任务成功' : '任务失败',
            rewardsGiven: success
          }
        })
        
        const completedMissions = success ? state.statistics.completedMissions + 1 : state.statistics.completedMissions
        const failedMissions = !success ? state.statistics.failedMissions + 1 : state.statistics.failedMissions
        
        return {
          missions: newMissions,
          retainers: newRetainers,
          statistics: {
            ...state.statistics,
            completedMissions,
            failedMissions
          }
        }
      }
      
      return state
    })
  },
  
  // 招募管理
  startRecruitment: (factionId, location, budget) => {
    const id = `recruitment_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newPlans = new Map(state.recruitmentPlans)
      newPlans.set(id, {
        id,
        factionId,
        target: {},
        budget: {
          money: budget,
          gifts: []
        },
        location,
        duration: 30,
        startedAt: now,
        estimatedCompletion: { year: 1046, month: 2, day: 1 },
        status: 'in_progress',
        results: {
          retainersRecruited: [],
          totalCost: 0
        }
      })
      
      return {
        recruitmentPlans: newPlans,
        statistics: {
          ...state.statistics,
          totalRecruitmentPlans: state.statistics.totalRecruitmentPlans + 1
        }
      }
    })
    
    return id
  },
  
  // 事件管理
  createEvent: (retainerId, type, description, details) => {
    const id = `event_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newEvents = new Map(state.events)
      newEvents.set(id, {
        id,
        retainerId,
        type,
        description,
        date: now,
        details
      })
      
      return { events: newEvents }
    })
  },
  
  // 统计
  updateStatistics: () => {
    set(state => ({
      statistics: {
        ...state.statistics
      }
    }))
  }
}))
